/**
 * TimesheetPanel - Time Entry Viewing and Export Component
 * 
 * Features:
 * - Daily/weekly time totals
 * - GPS trail map visualization
 * - Export as CSV with email option
 * - Admin view for all crew members
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  Card,
  CardContent,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  AccessTime as TimeIcon,
  Map as MapIcon,
  FileDownload as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  DirectionsCar as CarIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { getCyberColors } from '../theme/cyberColors';
import { telegram } from '../services/telegram';
import CyberSpinner from './CyberSpinner';

interface LocationEvent {
  type: 'start' | 'pause' | 'resume' | 'stop';
  lat: number | null;
  lng: number | null;
  timestamp: string;
}

interface TimeEntry {
  id: string;
  status: string;
  crewId: string | null;
  crewName: string | null;
  startTime: string;
  endTime: string | null;
  totalWorkSeconds: number;
  totalWorkHours: string;
  totalPauseSeconds: number;
  totalDistanceMeters: number | null;
  totalDistanceMiles: string | null;
  locationEvents: LocationEvent[];
}

interface DailyTotal {
  date: string;
  totalHours: string;
  totalMiles: string;
  entryCount: number;
}

interface MemberSummary {
  userId: string;
  username: string | null;
  totalHours: string;
  totalMiles: string;
  entries: TimeEntry[];
}

interface TimesheetPanelProps {
  crewId?: string;
  isAdmin?: boolean;
  darkMode?: boolean;
}

export function TimesheetPanel({ crewId, isAdmin = false, darkMode = true }: TimesheetPanelProps) {
  const cyber = getCyberColors(darkMode);
  
  // State
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [dailyTotals, setDailyTotals] = useState<DailyTotal[]>([]);
  const [weeklyTotalHours, setWeeklyTotalHours] = useState('0');
  const [weeklyTotalMiles, setWeeklyTotalMiles] = useState('0');
  const [memberSummaries, setMemberSummaries] = useState<MemberSummary[]>([]);
  const [expandedEntry, setExpandedEntry] = useState<string | null>(null);
  const [expandedMember, setExpandedMember] = useState<string | null>(null);
  const [showMapDialog, setShowMapDialog] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<TimeEntry | null>(null);
  const [viewTab, setViewTab] = useState<'personal' | 'crew'>(isAdmin ? 'crew' : 'personal');
  const [dateRange, setDateRange] = useState<'week' | 'month'>('week');
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info',
  });
  
  // Map refs
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);

  // Get headers for API calls
  const getHeaders = (): Record<string, string> => {
    const userId = telegram.getUserId();
    const username = telegram.getUserName();
    return {
      'Content-Type': 'application/json',
      ...(userId && { 'X-Telegram-User-Id': userId }),
      ...(username && { 'X-Telegram-Username': username }),
    };
  };

  // Get date range for API
  const getDateRange = () => {
    const now = new Date();
    let startDate: Date;
    
    if (dateRange === 'week') {
      const day = now.getDay();
      const diff = now.getDate() - day + (day === 0 ? -6 : 1);
      startDate = new Date(now.setDate(diff));
      startDate.setHours(0, 0, 0, 0);
    } else {
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    }
    
    const endDate = new Date();
    endDate.setHours(23, 59, 59, 999);
    
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
  };

  // Load time entries
  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const { startDate, endDate } = getDateRange();
      
      if (viewTab === 'crew' && crewId && isAdmin) {
        // Load crew entries (admin view)
        const response = await fetch(
          `/api/time-entries/crew/${crewId}?startDate=${startDate}&endDate=${endDate}`,
          { headers: getHeaders() }
        );
        if (!response.ok) throw new Error('Failed to load crew entries');
        
        const data = await response.json();
        setMemberSummaries(data.memberSummaries || []);
        setEntries([]);
      } else {
        // Load personal entries
        const response = await fetch(
          `/api/time-entries?startDate=${startDate}&endDate=${endDate}`,
          { headers: getHeaders() }
        );
        if (!response.ok) throw new Error('Failed to load entries');
        
        const data = await response.json();
        setEntries(data.entries || []);
        setDailyTotals(data.summary?.dailyTotals || []);
        setWeeklyTotalHours(data.summary?.weeklyTotalHours || '0');
        setWeeklyTotalMiles(data.summary?.weeklyTotalMiles || '0');
        setMemberSummaries([]);
      }
    } catch (error) {
      console.error('Load entries error:', error);
      setToast({ open: true, message: 'Failed to load timesheet', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [crewId, isAdmin, viewTab, dateRange]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  // Export CSV
  const handleExportCSV = async () => {
    try {
      const { startDate, endDate } = getDateRange();
      const response = await fetch(
        `/api/time-entries/export?startDate=${startDate}&endDate=${endDate}&format=csv`,
        { headers: getHeaders() }
      );
      
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `timesheet_${startDate.split('T')[0]}_to_${endDate.split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      setToast({ open: true, message: '📥 CSV downloaded', severity: 'success' });
      telegram.hapticFeedback('success');
    } catch (error) {
      setToast({ open: true, message: 'Export failed', severity: 'error' });
    }
  };

  // Email timesheet
  const handleEmailTimesheet = async () => {
    try {
      const { startDate, endDate } = getDateRange();
      const response = await fetch(
        `/api/time-entries/export?startDate=${startDate}&endDate=${endDate}&format=json`,
        { headers: getHeaders() }
      );
      
      if (!response.ok) throw new Error('Export failed');
      
      const data = await response.json();
      
      // Format email body
      const subject = encodeURIComponent(`Timesheet: ${startDate.split('T')[0]} to ${endDate.split('T')[0]}`);
      const body = encodeURIComponent(
        `Weekly Timesheet Summary\n` +
        `========================\n\n` +
        `Period: ${startDate.split('T')[0]} to ${endDate.split('T')[0]}\n\n` +
        `Total Hours: ${data.summary.totalHours}\n` +
        `Total Miles: ${data.summary.totalMiles}\n` +
        `Entries: ${data.summary.entryCount}\n\n` +
        `Daily Breakdown:\n` +
        data.entries.map((e: any) => 
          `  ${e.date}: ${e.hoursWorked}h, ${e.milesTraveled} mi`
        ).join('\n') +
        `\n\n---\nGenerated by ZiaWellNav`
      );
      
      // Try native share first, fallback to mailto
      if (navigator.share) {
        await navigator.share({
          title: `Timesheet: ${startDate.split('T')[0]} to ${endDate.split('T')[0]}`,
          text: decodeURIComponent(body),
        });
      } else {
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
      }
      
      telegram.hapticFeedback('success');
    } catch (error) {
      // User cancelled share or error
      console.log('Share cancelled or failed:', error);
    }
  };

  // Show GPS trail map
  const showGPSTrail = (entry: TimeEntry) => {
    setSelectedEntry(entry);
    setShowMapDialog(true);
  };

  // Initialize map for GPS trail
  useEffect(() => {
    if (showMapDialog && selectedEntry && mapRef.current && window.google?.maps) {
      const events = selectedEntry.locationEvents.filter(e => e.lat && e.lng);
      
      if (events.length === 0) {
        return;
      }

      // Calculate bounds
      const bounds = new google.maps.LatLngBounds();
      events.forEach(e => {
        if (e.lat && e.lng) {
          bounds.extend({ lat: e.lat, lng: e.lng });
        }
      });

      // Create map
      const map = new google.maps.Map(mapRef.current, {
        zoom: 12,
        center: bounds.getCenter(),
        mapTypeId: 'roadmap',
        disableDefaultUI: true,
        zoomControl: true,
      });
      
      map.fitBounds(bounds, 50);
      mapInstanceRef.current = map;

      // Draw path
      const path = events.map(e => ({ lat: e.lat!, lng: e.lng! }));
      new google.maps.Polyline({
        path,
        map,
        strokeColor: cyber.neonBlue,
        strokeWeight: 4,
        strokeOpacity: 0.8,
      });

      // Add markers for each event
      events.forEach((event) => {
        const color = event.type === 'start' ? cyber.neonGreen :
                      event.type === 'stop' ? cyber.neonRed :
                      event.type === 'pause' ? cyber.neonOrange : cyber.neonBlue;
        
        new google.maps.Marker({
          position: { lat: event.lat!, lng: event.lng! },
          map,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: event.type === 'start' || event.type === 'stop' ? 12 : 8,
            fillColor: color,
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2,
          },
          title: `${event.type} - ${new Date(event.timestamp).toLocaleTimeString()}`,
        });
      });
    }
  }, [showMapDialog, selectedEntry, cyber]);

  // Format time display
  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  };

  // Get event icon
  const getEventIcon = (type: string): React.ReactElement | undefined => {
    switch (type) {
      case 'start': return <PlayIcon sx={{ color: cyber.neonGreen, fontSize: 16 }} />;
      case 'pause': return <PauseIcon sx={{ color: cyber.neonOrange, fontSize: 16 }} />;
      case 'resume': return <PlayIcon sx={{ color: cyber.neonBlue, fontSize: 16 }} />;
      case 'stop': return <StopIcon sx={{ color: cyber.neonRed, fontSize: 16 }} />;
      default: return undefined;
    }
  };

  return (
    <Box>
      {/* Header with controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: cyber.textPrimary }}>
          <TimeIcon sx={{ color: cyber.neonBlue }} /> My Timesheet
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={loadEntries} size="small" sx={{ color: cyber.neonBlue }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* View tabs for admins */}
      {isAdmin && crewId && (
        <Tabs 
          value={viewTab} 
          onChange={(_, v) => setViewTab(v)}
          sx={{ mb: 2 }}
        >
          <Tab value="personal" label="My Time" />
          <Tab value="crew" label="Crew Time" />
        </Tabs>
      )}

      {/* Date range selector */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Chip
          label="This Week"
          onClick={() => setDateRange('week')}
          color={dateRange === 'week' ? 'primary' : 'default'}
          variant={dateRange === 'week' ? 'filled' : 'outlined'}
          size="small"
        />
        <Chip
          label="This Month"
          onClick={() => setDateRange('month')}
          color={dateRange === 'month' ? 'primary' : 'default'}
          variant={dateRange === 'month' ? 'filled' : 'outlined'}
          size="small"
        />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CyberSpinner size={40} />
        </Box>
      ) : viewTab === 'crew' && memberSummaries.length > 0 ? (
        /* Crew view for admins */
        <Box>
          {memberSummaries.map(member => (
            <Card key={member.userId} sx={{ mb: 2, backgroundColor: cyber.bgSecondary }}>
              <CardContent sx={{ pb: 1 }}>
                <Box 
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                  onClick={() => setExpandedMember(expandedMember === member.userId ? null : member.userId)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon sx={{ color: cyber.neonBlue }} />
                    <Typography sx={{ color: cyber.textPrimary, fontWeight: 600 }}>
                      {member.username || 'Unknown User'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip 
                      icon={<TimeIcon />} 
                      label={`${member.totalHours}h`} 
                      size="small"
                      sx={{ backgroundColor: `${cyber.neonGreen}20`, color: cyber.neonGreen }}
                    />
                    <Chip 
                      icon={<CarIcon />} 
                      label={`${member.totalMiles} mi`} 
                      size="small"
                      sx={{ backgroundColor: `${cyber.neonBlue}20`, color: cyber.neonBlue }}
                    />
                    {expandedMember === member.userId ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Box>
                </Box>
              </CardContent>
              <Collapse in={expandedMember === member.userId}>
                <Divider />
                <List dense>
                  {member.entries.map(entry => (
                    <ListItem key={entry.id}>
                      <ListItemText
                        primary={formatDate(entry.startTime)}
                        secondary={`${formatTime(entry.startTime)} - ${entry.endTime ? formatTime(entry.endTime) : 'In Progress'}`}
                        primaryTypographyProps={{ color: cyber.textPrimary }}
                        secondaryTypographyProps={{ color: cyber.textMuted }}
                      />
                      <Chip 
                        label={`${entry.totalWorkHours}h`} 
                        size="small" 
                        sx={{ mr: 1, backgroundColor: `${cyber.neonGreen}20`, color: cyber.neonGreen }}
                      />
                      {entry.locationEvents.length > 0 && (
                        <IconButton size="small" onClick={() => showGPSTrail(entry)}>
                          <MapIcon sx={{ color: cyber.neonBlue }} />
                        </IconButton>
                      )}
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </Card>
          ))}
        </Box>
      ) : (
        /* Personal view */
        <Box>
          {/* Weekly summary card */}
          <Paper sx={{ p: 2, mb: 2, backgroundColor: cyber.bgSecondary, border: `1px solid ${cyber.borderDefault}` }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
              <Box>
                <Typography variant="h4" sx={{ color: cyber.neonGreen, fontWeight: 700 }}>
                  {weeklyTotalHours}
                </Typography>
                <Typography variant="caption" sx={{ color: cyber.textMuted }}>
                  Hours This Week
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box>
                <Typography variant="h4" sx={{ color: cyber.neonBlue, fontWeight: 700 }}>
                  {weeklyTotalMiles}
                </Typography>
                <Typography variant="caption" sx={{ color: cyber.textMuted }}>
                  Miles This Week
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Export buttons */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportCSV}
              size="small"
              sx={{ flex: 1, borderColor: cyber.neonBlue, color: cyber.neonBlue }}
            >
              Export CSV
            </Button>
            <Button
              variant="outlined"
              startIcon={<ShareIcon />}
              onClick={handleEmailTimesheet}
              size="small"
              sx={{ flex: 1, borderColor: cyber.neonCyan, color: cyber.neonCyan }}
            >
              Share
            </Button>
          </Box>

          {/* Daily breakdown */}
          {dailyTotals.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: cyber.textSecondary, mb: 1 }}>
                Daily Breakdown
              </Typography>
              {dailyTotals.map(day => (
                <Box 
                  key={day.date} 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    py: 1, 
                    borderBottom: `1px solid ${cyber.borderDefault}` 
                  }}
                >
                  <Typography sx={{ color: cyber.textPrimary }}>
                    {new Date(day.date).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Typography sx={{ color: cyber.neonGreen }}>{day.totalHours}h</Typography>
                    <Typography sx={{ color: cyber.neonBlue }}>{day.totalMiles} mi</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {/* Entries list */}
          <Typography variant="subtitle2" sx={{ color: cyber.textSecondary, mb: 1 }}>
            Time Entries ({entries.length})
          </Typography>
          
          {entries.length === 0 ? (
            <Alert severity="info" sx={{ backgroundColor: `${cyber.neonBlue}10` }}>
              No time entries found for this period. Start the timer to begin tracking!
            </Alert>
          ) : (
            <List dense>
              {entries.map(entry => (
                <React.Fragment key={entry.id}>
                  <ListItem
                    sx={{ 
                      backgroundColor: cyber.bgSecondary, 
                      borderRadius: 1, 
                      mb: 1,
                      border: `1px solid ${entry.status === 'running' ? cyber.neonGreen : cyber.borderDefault}`,
                    }}
                  >
                    <ListItemIcon>
                      {entry.status === 'running' ? (
                        <PlayIcon sx={{ color: cyber.neonGreen }} />
                      ) : entry.status === 'paused' ? (
                        <PauseIcon sx={{ color: cyber.neonOrange }} />
                      ) : (
                        <TimeIcon sx={{ color: cyber.textMuted }} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ color: cyber.textPrimary }}>
                            {formatDate(entry.startTime)}
                          </Typography>
                          {entry.status !== 'completed' && (
                            <Chip 
                              label={entry.status} 
                              size="small" 
                              sx={{ 
                                backgroundColor: entry.status === 'running' ? `${cyber.neonGreen}30` : `${cyber.neonOrange}30`,
                                color: entry.status === 'running' ? cyber.neonGreen : cyber.neonOrange,
                                textTransform: 'capitalize',
                                height: 20,
                              }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" sx={{ color: cyber.textMuted }}>
                          {formatTime(entry.startTime)} - {entry.endTime ? formatTime(entry.endTime) : 'In Progress'}
                          {entry.crewName && ` • ${entry.crewName}`}
                        </Typography>
                      }
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip 
                        label={`${entry.totalWorkHours}h`} 
                        size="small"
                        sx={{ backgroundColor: `${cyber.neonGreen}20`, color: cyber.neonGreen }}
                      />
                      {entry.totalDistanceMiles && (
                        <Chip 
                          label={`${entry.totalDistanceMiles} mi`} 
                          size="small"
                          sx={{ backgroundColor: `${cyber.neonBlue}20`, color: cyber.neonBlue }}
                        />
                      )}
                      <IconButton 
                        size="small" 
                        onClick={() => setExpandedEntry(expandedEntry === entry.id ? null : entry.id)}
                      >
                        {expandedEntry === entry.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                  </ListItem>
                  
                  {/* Expanded entry details */}
                  <Collapse in={expandedEntry === entry.id}>
                    <Box sx={{ ml: 7, mr: 2, mb: 2, p: 1, backgroundColor: cyber.bgPrimary, borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ color: cyber.textSecondary, fontWeight: 600 }}>
                        GPS Trail ({entry.locationEvents.length} points)
                      </Typography>
                      {entry.locationEvents.length > 0 ? (
                        <>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1, mb: 1 }}>
                            {entry.locationEvents.map((event, idx) => (
                              <Chip
                                key={idx}
                                icon={getEventIcon(event.type)}
                                label={formatTime(event.timestamp)}
                                size="small"
                                variant="outlined"
                                sx={{ borderColor: cyber.borderDefault }}
                              />
                            ))}
                          </Box>
                          <Button
                            size="small"
                            startIcon={<MapIcon />}
                            onClick={() => showGPSTrail(entry)}
                            sx={{ color: cyber.neonBlue }}
                          >
                            View Map Trail
                          </Button>
                        </>
                      ) : (
                        <Typography variant="body2" sx={{ color: cyber.textMuted, mt: 1 }}>
                          No GPS data recorded for this entry
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      )}

      {/* GPS Trail Map Dialog */}
      <Dialog
        open={showMapDialog}
        onClose={() => {
          telegram.hapticFeedback('light');
          setShowMapDialog(false);
          setSelectedEntry(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: cyber.bgPrimary,
            border: `1px solid ${cyber.neonBlue}`,
          }
        }}
      >
        <DialogTitle sx={{ color: cyber.textPrimary, display: 'flex', alignItems: 'center', gap: 1 }}>
          <MapIcon sx={{ color: cyber.neonBlue }} />
          GPS Trail - {selectedEntry && formatDate(selectedEntry.startTime)}
        </DialogTitle>
        <DialogContent>
          {selectedEntry?.locationEvents.filter(e => e.lat && e.lng).length === 0 ? (
            <Alert severity="info">No GPS coordinates recorded for this entry</Alert>
          ) : (
            <Box
              ref={mapRef}
              sx={{ width: '100%', height: 400, borderRadius: 1, overflow: 'hidden' }}
            />
          )}
          {selectedEntry && (
            <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Chip 
                icon={<TimeIcon />} 
                label={`${selectedEntry.totalWorkHours} hours`}
                sx={{ backgroundColor: `${cyber.neonGreen}20`, color: cyber.neonGreen }}
              />
              {selectedEntry.totalDistanceMiles && (
                <Chip 
                  icon={<CarIcon />} 
                  label={`${selectedEntry.totalDistanceMiles} miles`}
                  sx={{ backgroundColor: `${cyber.neonBlue}20`, color: cyber.neonBlue }}
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { telegram.hapticFeedback('light'); setShowMapDialog(false); }} sx={{ color: cyber.textMuted }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ marginTop: '60px' }}
      >
        <Alert severity={toast.severity} variant="filled">
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default TimesheetPanel;
